import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '@mts/ui';
import { authClient } from '../lib/authClient';
import { getApiBaseUrl, getTenantSlugFromHostname } from '../services/api';
import { getMagicLinkErrorMessage } from '../utils/magicLinkErrors';
import { usePublicTenant } from '../hooks/usePublicTenant';

function RegisterPage() {
  const toast = useFeedback();
  const [searchParams] = useSearchParams();
  const { data: session, isPending } = authClient.useSession();
  const { spotlightName } = usePublicTenant();
  const [form, setForm] = useState({ email: '' });
  const [error, setError] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const claimListingSlug = searchParams.get('listingSlug') || searchParams.get('listingId');
  const promotionCode = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode') || '';
  const claimReturnPath = claimListingSlug ? `/listings/${encodeURIComponent(claimListingSlug)}/claim${promotionCode ? `?promo=${encodeURIComponent(promotionCode)}` : ''}` : '/profile';
  const loginParams = new URLSearchParams();
  if (claimListingSlug) loginParams.set('listingSlug', claimListingSlug);
  if (promotionCode) loginParams.set('promo', promotionCode);
  const queryErrorMessage = getMagicLinkErrorMessage(searchParams.get('error'));
  const activeErrorMessage = error || queryErrorMessage;

  if (session?.user?.id) {
    return <Navigate to={claimReturnPath} replace />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');


    const tenantSlug = getTenantSlugFromHostname();
    const validateHeaders = { 'Content-Type': 'application/json' };
    if (tenantSlug) {
      validateHeaders['X-Tenant-Slug'] = tenantSlug;
    }
    const validateResponse = await fetch(`${getApiBaseUrl()}/auth/validate-email`, {
      method: 'POST',
      headers: validateHeaders,
      credentials: 'include',
      body: JSON.stringify({ email: form.email }),
    });

    const validatePayload = await validateResponse.json().catch(() => ({}));

    if (!validateResponse.ok || !validatePayload?.valid) {
      setSubmitting(false);
      setError(validatePayload?.error || 'Please enter a valid email address.');
      return;
    }

    const { error: magicLinkError } = await authClient.signIn.magicLink({
      email: form.email,
      callbackURL: claimReturnPath,
      newUserCallbackURL: claimReturnPath,
      errorCallbackURL: '/register',
    });

    setSubmitting(false);

    if (magicLinkError) {
      setError(magicLinkError.message || 'Unable to send sign-in link.');
      return;
    }

    toast.success(`We sent a secure sign-in link to ${form.email}. Open your email to continue.`, { id: 'action:RegisterPage:handleSubmit' });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-4 py-12">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(42rem 42rem at 8% -10%, color-mix(in srgb, var(--primary) 18%, transparent), transparent 65%), radial-gradient(32rem 32rem at 92% 10%, color-mix(in srgb, var(--secondary) 16%, transparent), transparent 68%)',
        }}
      />

      <div className="relative mx-auto flex w-full max-w-md items-center justify-center">
        <Card
          padding="lg"
          shadow="lg"
          className="w-full border"
          style={{
            backgroundColor: 'var(--surface)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <h1 className="font-ui text-2xl font-semibold" style={{ color: 'var(--primary)' }}>
                Create Account
              </h1>
              <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                Create your {spotlightName || 'My Town Spotlight'} account.
              </p>
            </div>

            {activeErrorMessage ? (
              <InlineFeedback variant="error"
                className="rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: 'color-mix(in srgb, var(--danger) 40%, var(--border))',
                  color: 'var(--danger)',
                  backgroundColor: 'color-mix(in srgb, var(--danger) 10%, var(--surface))',
                }}
              >
                {activeErrorMessage}
              </InlineFeedback>
            ) : null}



            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
                disabled={submitting || isPending}
                className="border-[var(--ui-input-border)] text-[var(--ui-input-text)] placeholder:text-[color:color-mix(in_srgb,var(--ui-input-text)_58%,white)] focus:border-[var(--primary)] focus:ring-[var(--primary)]"
              />
              <Button type="submit" className="w-full" disabled={submitting || isPending} loading={submitting}>
                {submitting ? 'Sending link...' : 'Verify My Email'}
              </Button>
            </form>

            <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
              Already have an account?{' '}
              <Link
                to={`/login${loginParams.size ? `?${loginParams.toString()}` : ''}`}
                className="font-medium underline decoration-2 underline-offset-2 transition-colors"
                style={{ color: 'var(--primary)' }}
              >
                Sign in
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default RegisterPage;
